// Pure helpers for the ESG auditor evaluation screens (kept out of the components so the rules are testable).

export const TASK_KEY_LABELS = {
  task: 'Individual task',
  case: 'Case study analysis',
  'case:adani': 'Adani case',
  'case:blackrock': 'BlackRock case',
};

// "754" seconds -> "12 min", "3720" -> "1 h 02 min"; null (nothing reported) -> "-".
export function formatSpent(seconds) {
  if (seconds === null || seconds === undefined) return '-';
  if (seconds < 60) return seconds === 0 ? '0 min' : `${seconds} sec`;
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes} min`;
  const hours = Math.floor(minutes / 60);
  const rest = minutes % 60;
  return rest === 0 ? `${hours} h` : `${hours} h ${String(rest).padStart(2, '0')} min`;
}

export const taskKeyLabel = (key) => TASK_KEY_LABELS[key] || 'Task';

// A council's evaluation: draft = nothing submitted yet, partial = some steps submitted, approved = every step done.
export const STATUS_LABELS = {
  draft: 'Not started',
  partial: 'In progress',
  approved: 'Complete',
};

export const statusLabel = (status) => STATUS_LABELS[status] || 'Not started';

// status-pill modifier classes that already exist in styles.css
export const statusPillClass = (status) => {
  if (status === 'approved') return 'active';
  if (status === 'partial') return 'planned';
  return 'cancelled';
};

/** The squad step's form from a workspace payload: the saved values (a reopened step starts from them), else empty. */
export function squadDraftFrom(workspace) {
  const e = workspace?.evaluation;
  return {
    rating: e?.session_rating ?? null,
    strengths: [...(e?.session_strengths || [])],
    coaching: [...(e?.session_coaching || [])],
    comment: e?.session_comment || '',
  };
}

export function chairDraftFrom(workspace) {
  const e = workspace?.evaluation;
  return {
    rating: e?.chair_rating ?? null,
    strengths: [...(e?.chair_strengths || [])],
    coaching: [...(e?.chair_coaching || [])],
    comment: e?.chair_comment || '',
  };
}

/** One member's task form: a row per task key, from rows already saved (a reopened task), else undecided. */
export function taskDraftFrom(member) {
  const draft = {};
  (member?.task_keys || []).forEach((key) => {
    const saved = (member.task_evaluations || []).find((t) => t.task_key === key && t.outcome !== 'not_submitted');
    draft[key] = saved
      ? { confirmed: Boolean(saved.confirmed), rating: saved.rating ?? null, note: saved.note || '' }
      : { confirmed: null, rating: null, note: '' };
  });
  return draft;
}

/** Every key decided, and a score on every confirmed case study - the same rule the server enforces. */
export function taskReady(keys, draft) {
  return (keys || []).every((key) => {
    const row = draft?.[key];
    if (!row || row.confirmed === null || row.confirmed === undefined) return false;
    return key === 'task' || !row.confirmed || (row.rating !== null && row.rating !== undefined);
  });
}

/** Request body for one member's task step. A rating only travels with a confirmed case study. */
export function taskPayload(keys, draft) {
  return {
    tasks: (keys || []).map((key) => {
      const row = draft?.[key] || {};
      return {
        task_key: key,
        confirmed: Boolean(row.confirmed),
        rating: row.confirmed && key !== 'task' && row.rating !== null && row.rating !== undefined && row.rating !== '' ? Number(row.rating) : null,
        note: row.note ? row.note : null,
      };
    }),
  };
}

/** Half-point options for scoring a case study: 0, 0.5 ... 10. */
export const caseScoreOptions = (range) => {
  const { min, max, step } = range || { min: 0, max: 10, step: 0.5 };
  const options = [];
  for (let v = min; v <= max + 1e-9; v += step) options.push(Math.round(v * 10) / 10);
  return options;
};

// Same status-badge colours the Glosco evaluation screens use.
export const statusBadgeClass = (status) => {
  if (status === 'approved') return 'completed';
  return 'draft';
};

export const formatDateTime = (iso) =>
  iso ? new Date(iso).toLocaleString(undefined, { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '-';

export const formatDate = (iso) =>
  iso ? new Date(iso).toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' }) : '-';

export const COUNCIL_NUMBERS = Array.from({ length: 10 }, (_, i) => i + 1);
